<script setup lang="ts">
import { reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

interface RuleForm { old_password: string; new_password: string; repeat_password: string; }

const validateConfirmPassword = (rule: any, value: any, callback: any) => {
  if (value === "") callback(new Error("Please confirm your password"));
  else if (value !== ruleForm.new_password) callback(new Error("Passwords do not match"));
  else callback();
};

const loading = ref(false);
const formError = ref("");
const showOld = ref(false);
const showNew = ref(false);
const showConfirm = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({ old_password: "", new_password: "", repeat_password: "" });

const rules = reactive<FormRules<RuleForm>>({
  old_password: [{ required: true, message: "Please enter your current password", trigger: "blur" }],
  new_password: [{ required: true, message: "Please enter a new password", trigger: "blur" }, { min: 6, message: "Password must be at least 6 characters", trigger: "blur" }],
  repeat_password: [{ validator: validateConfirmPassword, trigger: "blur" }],
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => { if (valid) { loading.value = true; postData(); } });
};

const postData = async () => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}changepassword?token=${store.getToken}`,
      { old_password: ruleForm.old_password, new_password: ruleForm.new_password, company_id: store.getUserData.company_id, user_id: store.getUserData.user_id }
    );
    loading.value = false;
    if (response.status == 200 && response.data.status) {
      ElMessage({ message: "Password updated successfully", type: "success" });
      router.push("/request");
    } else {
      ElMessage({ message: response.data.message || "Password update failed.", type: "error" });
    }
  } catch {
    loading.value = false;
    formError.value = "Something went wrong, please try again.";
  }
};

const logwat = () => {
  store.setAuthStatus(false); store.$reset();
  localStorage.removeItem("_panda"); router.push("/login");
};
</script>

<template>
  <div class="ac-card" v-loading="loading">
    <div class="ac-panel-head">
      <div>
        <h2 class="ac-panel-title">Change Password</h2>
        <p class="ac-panel-desc">Keep your account secure with a strong password</p>
      </div>
    </div>

    <div class="ac-card-body">
      <div class="ac-error" v-if="formError">{{ formError }}</div>

      <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules" label-width="0px" status-icon>
        <div class="ac-section-label">Current password</div>
        <div class="ac-grid ac-grid--one">
          <div class="ac-field">
            <label class="ac-label" for="pw-old">Current Password <span class="ac-req">*</span></label>
            <el-form-item prop="old_password" class="ac-fi">
              <div class="ac-input-wrap">
                <input id="pw-old" v-model="ruleForm.old_password" class="ac-input" :type="showOld ? 'text' : 'password'" placeholder="Enter current password" autocomplete="current-password" />
                <button type="button" class="ac-eye" :aria-label="showOld ? 'Hide password' : 'Show password'" @click="showOld = !showOld">
                  <svg v-if="!showOld" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </el-form-item>
          </div>
        </div>

        <div class="ac-section-label">New password</div>
        <div class="ac-grid">
          <div class="ac-field">
            <label class="ac-label" for="pw-new">New Password <span class="ac-req">*</span></label>
            <el-form-item prop="new_password" class="ac-fi">
              <div class="ac-input-wrap">
                <input id="pw-new" v-model="ruleForm.new_password" class="ac-input" :type="showNew ? 'text' : 'password'" placeholder="At least 6 characters" autocomplete="new-password" />
                <button type="button" class="ac-eye" :aria-label="showNew ? 'Hide password' : 'Show password'" @click="showNew = !showNew">
                  <svg v-if="!showNew" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </el-form-item>
          </div>
          <div class="ac-field">
            <label class="ac-label" for="pw-repeat">Confirm New Password <span class="ac-req">*</span></label>
            <el-form-item prop="repeat_password" class="ac-fi">
              <div class="ac-input-wrap">
                <input id="pw-repeat" v-model="ruleForm.repeat_password" class="ac-input" :type="showConfirm ? 'text' : 'password'" placeholder="Repeat new password" autocomplete="new-password" />
                <button type="button" class="ac-eye" :aria-label="showConfirm ? 'Hide password' : 'Show password'" @click="showConfirm = !showConfirm">
                  <svg v-if="!showConfirm" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </el-form-item>
          </div>
        </div>

        <div class="ac-actions">
          <button class="ac-btn" @click.prevent="submitForm(ruleFormRef)" :disabled="loading">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Update Password
          </button>
        </div>
      </el-form>
    </div>
  </div>
</template>
